/**
 * How loud a stretch of decoded audio is, for levelling (Dax, 2026-10-03): its loudest LEVEL_WINDOW_MS of
 * RMS, stepped every LEVEL_HOP_MS, and its sample peak, both in dBFS over every channel. The loudest
 * stretch rather than the whole: a click padded with silence measures as almost nothing over its file,
 * and a pour of the same peak sounds far louder, so neither the average nor the peak alone evens them out.
 */
import { LEVEL_HOP_MS, LEVEL_WINDOW_MS } from '@studio/contracts';

export interface Loudness { loudestDb: number; peakDb: number }

export function measureLoudness(channels: Float32Array[], sampleRate: number, fromMs: number, durationMs: number): Loudness {
  const length = channels[0]?.length ?? 0;
  const a = Math.max(0, Math.min(length, Math.floor((fromMs / 1000) * sampleRate)));
  const b = Math.max(a, Math.min(length, Math.floor(((fromMs + durationMs) / 1000) * sampleRate)));
  // Per-sample energy, summed over channels and averaged, so stereo and mono read alike.
  const energy = new Float64Array(b - a);
  let peak = 0;
  for (const data of channels) {
    for (let i = a; i < b; i++) {
      const v = data[i]!;
      energy[i - a]! += (v * v) / channels.length;
      const abs = Math.abs(v);
      if (abs > peak) peak = abs;
    }
  }
  // A stretch shorter than one window is measured as one window over what there is.
  const win = Math.max(1, Math.min(energy.length, Math.round((LEVEL_WINDOW_MS / 1000) * sampleRate)));
  const hop = Math.max(1, Math.round((LEVEL_HOP_MS / 1000) * sampleRate));
  let loudest = 0;
  for (let start = 0; start + win <= energy.length; start += hop) {
    let sum = 0;
    for (let i = start; i < start + win; i++) sum += energy[i]!;
    loudest = Math.max(loudest, sum / win);
  }
  return {
    loudestDb: loudest > 0 ? 10 * Math.log10(loudest) : -Infinity,
    peakDb: peak > 0 ? 20 * Math.log10(peak) : -Infinity,
  };
}

/** Where a decoded file's sound begins: the first sample on any channel louder than `floorDb`, in ms; null when it never does. */
export function firstSignalMs(channels: Float32Array[], sampleRate: number, floorDb: number): number | null {
  const floor = Math.pow(10, floorDb / 20);
  const length = channels[0]?.length ?? 0;
  for (let i = 0; i < length; i++) {
    for (const data of channels) if (Math.abs(data[i]!) > floor) return Math.floor((i / sampleRate) * 1000);
  }
  return null;
}
